import DOMPurify from "dompurify";
import {
  Bird, HardHat, LayoutDashboard, ChevronRight,
  Zap, BarChart3, Globe, Users, Check, ArrowRight,
  Star, Lock, Fuel, MapPin, Volume2, Mic,
  Monitor, Smartphone, Link2, Truck
} from "lucide-react";

const EDITORIAL_IMAGES = {
  field: "https://images.unsplash.com/photo-1694521787162-5373b598945c?w=800&q=80",
  decision: "https://images.unsplash.com/photo-1666660165383-0c63c6947e46?w=800&q=80",
  birdseye: "https://images.pexels.com/photos/8782658/pexels-photo-8782658.jpeg?auto=compress&cs=tinysrgb&w=800",
  freeflow: "https://images.unsplash.com/photo-1627054458477-40ba0148b76a?w=800&q=80",
};

function EditorialSection({ image, icon: Icon, tag, line1, line2, line3, p1, p2, badges, reversed, iconPos, children }) {
  const imgBlock = (
    <div className={`lg:col-span-5 relative ${reversed ? "order-1 lg:order-2" : ""}`}>
      <div className="relative overflow-hidden rounded-2xl shadow-xl w-full aspect-[4/5]">
        <img src={image} alt={tag} className="w-full h-full object-cover saturate-[1.3]" style={{ filter: "contrast(1.1)" }} />
        <div className="absolute inset-0 pointer-events-none" style={{
          backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
          WebkitMaskImage: "linear-gradient(to bottom, black 0%, transparent 15%, transparent 85%, black 100%)",
          maskImage: "linear-gradient(to bottom, black 0%, transparent 15%, transparent 85%, black 100%)"
        }} />
      </div>
      <div className={`absolute ${iconPos || "-bottom-3 -right-3"} w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center shadow-lg`}>
        <Icon className="w-7 h-7 text-white" strokeWidth={1.5} />
      </div>
    </div>
  );

  const textBlock = (
    <div className={`lg:col-span-7 pt-4 ${reversed ? "order-2 lg:order-1" : ""}`}>
      <span className="text-[10px] font-bold text-red-500 uppercase tracking-[0.3em] block mb-6">{tag}</span>
      <h2 className="text-4xl lg:text-5xl tracking-tight leading-[1] mb-8">
        <span className="font-extralight text-slate-400">{line1}</span><br />
        <span className="font-black text-slate-900">{line2}</span>
        {line3 && <>{" "}<span className="font-black text-red-600">{line3}</span></>}
      </h2>
      <p className="text-base text-slate-500 font-light leading-[1.8] mb-5 max-w-lg" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p1) }} />
      <p className="text-base text-slate-500 font-light leading-[1.8] mb-8 max-w-lg" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p2) }} />
      {badges && (
        <div className="flex gap-2 flex-wrap">
          {badges.map((b, i) => (
            <span key={i} className={`px-3 py-1.5 border rounded-full text-[10px] font-bold uppercase tracking-wider ${i === 0 ? "bg-red-50 border-red-200 text-red-600" : "bg-slate-50 border-slate-200 text-slate-500"}`}>{b}</span>
          ))}
        </div>
      )}
      {children}
    </div>
  );

  return (
    <section className={`py-24 px-6 overflow-hidden ${reversed ? "bg-slate-50/50" : ""}`}>
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        {reversed ? <>{textBlock}{imgBlock}</> : <>{imgBlock}{textBlock}</>}
      </div>
    </section>
  );
}

export function EditorialSections({ t, nav }) {
  return (
    <>
      <EditorialSection image={EDITORIAL_IMAGES.field} icon={HardHat}
        tag={t("section1_tag")} line1={t("section1_line1")} line2={t("section1_line2")}
        p1={t("section1_p1")} p2={t("section1_p2")}
        badges={["Machinist App", "Echo Tank", "Driver Feedback"]}
      />
      <EditorialSection image={EDITORIAL_IMAGES.decision} icon={Zap} reversed
        iconPos="-bottom-3 -left-3"
        tag={t("section2_tag")} line1={t("section2_line1")} line2={t("section2_line2")} line3={t("section2_line3")}
        p1={t("section2_p1")} p2={t("section2_p2")}
        badges={["Hub Invitations", "Contractor Assignment", "Instant Checks"]}
      />
      <EditorialSection image={EDITORIAL_IMAGES.birdseye} icon={Bird}
        iconPos="-top-3 -right-3"
        tag={t("section3_tag")} line1={t("section3_line1")} line2={t("section3_line2")} line3={t("section3_line3")}
        p1={t("section3_p1")} p2={t("section3_p2")}
        badges={["Planner Dashboard", "Route Planning", "Service Portal"]}
      />
      <EditorialSection image={EDITORIAL_IMAGES.freeflow} icon={BarChart3} reversed
        iconPos="-bottom-3 -left-3"
        tag={t("section4_tag")} line1={t("section4_line1")} line2={t("section4_line2")} line3={t("section4_line3")}
        p1={t("section4_p1")} p2={t("section4_p2")}>
        <button onClick={() => nav("/auth")}
          className="btn-industrial px-8 py-4 bg-red-600 text-white rounded-xl text-sm font-black shadow-lg hover:bg-red-500 hover:shadow-xl transition-all">
          {t("get_started_free")} <ArrowRight className="w-5 h-5" />
        </button>
      </EditorialSection>
    </>
  );
}

export function SocialProofSection({ t }) {
  const stats = [
    { value: "150+", label: t("stat_hubs") },
    { value: "1,200+", label: t("stat_drivers") },
    { value: "50M+", label: t("stat_tracked") },
    { value: "9", label: t("stat_languages") },
  ];
  return (
    <section className="py-14 border-y border-slate-200 bg-slate-50/50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {stats.map((stat, i) => (
            <div key={i} className="animate-fadeInUp" style={{ animationDelay: `${i * 0.1}s` }}>
              <span className="text-3xl md:text-4xl font-black text-red-600 font-mono block">{stat.value}</span>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-2 block">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TestimonialSection({ t }) {
  return (
    <section className="py-28 px-6 bg-white">
      <div className="max-w-4xl mx-auto text-center">
        <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg">
          <Star className="w-8 h-8 text-white" />
        </div>
        <blockquote className="text-2xl md:text-3xl tracking-tight leading-snug mb-8">
          <span className="font-light text-slate-400">"</span>
          <span className="font-bold text-slate-900" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t("testimonial")) }} />
          <span className="font-light text-slate-400">"</span>
        </blockquote>
        <p className="text-sm font-bold text-slate-400 uppercase tracking-wider">{t("testimonial_author")}</p>
      </div>
    </section>
  );
}

export function VoiceFeaturesSection({ t }) {
  return (
    <section className="py-12 px-6 bg-slate-950" data-testid="voice-features-section">
      <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-red-600 rounded-2xl flex items-center justify-center shadow-lg shadow-red-600/30">
            <Volume2 className="w-7 h-7 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-black text-white tracking-tight">{t("voice_title") || "Voice-Powered Hub"}</h3>
            <p className="text-sm text-slate-400 font-light">{t("voice_desc") || "Spoken notifications, dictate requests by voice. Machinist speaks — Philogic creates."}</p>
          </div>
        </div>
        <div className="flex gap-4">
          {[
            { icon: Volume2, color: "text-lime-400", label: t("voice_tts") || "TTS Alerts" },
            { icon: Mic, color: "text-red-400", label: t("voice_stt") || "Voice Dictation" },
            { icon: Bird, color: "text-amber-400", label: t("voice_ai") || "AI Parse" },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl">
              <item.icon className={`w-4 h-4 ${item.color}`} />
              <span className="text-xs font-bold text-slate-300">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PricingSection({ pricing, pricingRef, t, nav, handleCheckout }) {
  return (
    <section ref={pricingRef} className="py-28 px-6 bg-slate-50 border-y border-slate-200" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl tracking-tighter mb-4">
            <span className="font-light text-slate-400">{t("choose_your_plan").split(" ").slice(0, -1).join(" ")}</span>{" "}
            <span className="font-black text-slate-900">{t("choose_your_plan").split(" ").slice(-1)[0]}</span>
          </h2>
          <p className="text-base text-slate-400 font-light">{t("pricing_desc")}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {pricing.map((plan, i) => (
            <div key={plan.id} data-testid={`price-card-${plan.id}`}
              className={`relative p-8 rounded-2xl border-2 transition-all animate-fadeInUp ${plan.highlight ? "bg-white border-red-400 shadow-lg" : "bg-white border-slate-200 hover:border-slate-300"}`}
              style={{ animationDelay: `${i * 0.1}s` }}>
              {plan.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-red-600 text-white px-5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider">
                  {t("best_value")}
                </div>
              )}
              <h3 className="text-lg font-bold text-slate-900 uppercase tracking-tight">{plan.name}</h3>
              <div className="mt-4 mb-6 flex items-end gap-1">
                {plan.price === 0 ? (
                  <div>
                    <span className="text-4xl font-black text-red-600 font-mono">{t("free_word")}</span>
                    <p className="text-xs text-slate-400 font-bold mt-1">{t("trial_24h_limit")}</p>
                  </div>
                ) : (
                  <>
                    <span className="text-4xl font-black text-slate-900 font-mono">{"\u20AC"}{plan.price}</span>
                    <span className="text-sm text-slate-400 font-bold mb-1">{plan.period}</span>
                  </>
                )}
              </div>
              <ul className="space-y-2.5 mb-6">
                {plan.features.map((f, fi) => (
                  <li key={fi} className="flex items-center gap-2 text-sm text-slate-600 font-medium">
                    <Check className="w-4 h-4 text-red-500 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              {plan.locked && plan.locked.length > 0 && (
                <div className="mb-6 pt-4 border-t border-slate-200">
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-2">{t("upgrade_to_unlock")}</p>
                  <ul className="space-y-1.5">
                    {plan.locked.map((f, fi) => (
                      <li key={fi} className="flex items-center gap-2 text-xs text-slate-400 font-medium">
                        <Lock className="w-3 h-3 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <button data-testid={`checkout-${plan.id}`} onClick={() => plan.price === 0 ? nav("/auth") : handleCheckout(plan.id)}
                className={`btn-industrial w-full py-4 rounded-xl text-sm font-black ${plan.highlight ? "bg-red-600 text-white shadow-md hover:bg-red-500" : "bg-slate-100 text-slate-800 hover:bg-slate-200"}`}>
                {plan.cta} <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <p className="text-center mt-8 text-sm text-slate-400 font-medium">
          {t("enterprise_line")} <button onClick={() => {}} className="text-red-600 font-bold hover:underline">{t("contact_us")}</button>
        </p>
      </div>
    </section>
  );
}

export function FAQSection({ t }) {
  const faqs = [
    { q: t("faq_q1"), a: t("faq_a1") },
    { q: t("faq_q2"), a: t("faq_a2") },
    { q: t("faq_q3"), a: t("faq_a3") },
    { q: t("faq_q4"), a: t("faq_a4") },
    { q: t("faq_q5"), a: t("faq_a5") },
  ];
  return (
    <section className="py-24 px-6 bg-white" data-testid="faq-section">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl tracking-tighter mb-3">
            <span className="font-light text-slate-400">{t("faq_title_light")}</span>{" "}
            <span className="font-black text-slate-900">{t("faq_title_bold")}</span>
          </h2>
        </div>
        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <details key={i} data-testid={`faq-item-${i}`} className="group border border-slate-200 rounded-xl overflow-hidden">
              <summary className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-slate-50 transition-colors">
                <span className="text-sm font-bold text-slate-800 pr-4">{faq.q}</span>
                <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 transition-transform group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-4">
                <p className="text-sm text-slate-500 leading-relaxed">{faq.a}</p>
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CTASection({ t, nav }) {
  return (
    <section className="py-24 px-6 bg-red-600">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-4xl md:text-5xl tracking-tighter mb-4">
          <span className="font-light text-red-200">{t("cta_line1")}</span><br />
          <span className="font-black text-white">{t("cta_line2")}</span>
        </h2>
        <p className="text-lg text-red-100/70 font-light mb-10">{t("cta_desc")}</p>
        <button data-testid="cta-start-btn" onClick={() => nav("/auth")}
          className="btn-industrial px-12 py-5 bg-white text-red-600 rounded-xl text-sm font-black shadow-2xl hover:bg-red-50 transition-all">
          {t("launch_pigeon")} <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </section>
  );
}

export function LandingFooter({ t, nav, pricingRef, setActivePage }) {
  return (
    <footer className="bg-white border-t border-slate-200 py-12 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-red-600 rounded flex items-center justify-center"><Bird className="w-4 h-4 text-white" /></div>
              <span className="text-sm font-black uppercase tracking-tight">Pigeon Supply</span>
            </div>
            <p className="text-xs text-slate-400 font-medium leading-relaxed">{t("footer_desc")}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4" role="heading" aria-level="3">{t("product")}</p>
            <ul className="space-y-2">
              <li><button onClick={() => pricingRef.current?.scrollIntoView({behavior:"smooth"})} className="text-xs text-slate-400 hover:text-red-600 font-medium transition-colors">{t("nav_pricing")}</button></li>
              <li><button onClick={() => nav("/")} className="text-xs text-slate-400 hover:text-red-600 font-medium transition-colors">Demo</button></li>
            </ul>
          </div>
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4" role="heading" aria-level="3">{t("legal")}</p>
            <ul className="space-y-2">
              <li><button data-testid="footer-terms" onClick={() => setActivePage("terms")} className="text-xs text-slate-400 hover:text-red-600 font-medium transition-colors">{t("terms_of_service")}</button></li>
              <li><button data-testid="footer-privacy" onClick={() => setActivePage("privacy")} className="text-xs text-slate-400 hover:text-red-600 font-medium transition-colors">{t("privacy_policy")}</button></li>
              <li><button data-testid="footer-refund" onClick={() => setActivePage("refund")} className="text-xs text-slate-400 hover:text-red-600 font-medium transition-colors">{t("refund_policy")}</button></li>
              <li><button data-testid="footer-gdpr" onClick={() => nav("/gdpr")} className="text-xs text-slate-400 hover:text-red-600 font-medium transition-colors">{t("gdpr_cookies")}</button></li>
            </ul>
          </div>
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4" role="heading" aria-level="3">{t("support")}</p>
            <ul className="space-y-2">
              <li><button data-testid="footer-contact" onClick={() => setActivePage("contact")} className="text-xs text-slate-400 hover:text-red-600 font-medium transition-colors">{t("contact_us")}</button></li>
              <li><a href="mailto:support@onepigeon.app" className="text-xs text-slate-400 hover:text-red-600 font-medium transition-colors">support@onepigeon.app</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">&copy; 2026 Pigeon Supply. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Lock className="w-4 h-4 text-slate-400" />
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Secured by Stripe</span>
            <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/ba/Stripe_Logo%2C_revised_2016.svg/120px-Stripe_Logo%2C_revised_2016.svg.png" alt="Stripe" className="h-5 opacity-40" />
          </div>
        </div>
      </div>
    </footer>
  );
}

export function TeamAssemblySection({ t }) {
  const combos = [
    {
      title: t("team_combo_a_title"), desc: t("team_combo_a_desc"),
      youLabel: t("team_combo_a_you"), inviteLabel: t("team_combo_a_invite"),
      youRoles: [
        { label: t("team_slot_planner"), icon: LayoutDashboard, color: "bg-lime-500", text: "text-slate-950" },
        { label: t("team_slot_machinist"), icon: HardHat, color: "bg-amber-500", text: "text-slate-950" },
      ],
      inviteRoles: [{ label: t("team_slot_driver"), icon: Truck, color: "bg-red-600", text: "text-white" }],
    },
    {
      title: t("team_combo_b_title"), desc: t("team_combo_b_desc"),
      youLabel: t("team_combo_b_you"), inviteLabel: t("team_combo_b_invite"),
      youRoles: [
        { label: t("team_slot_planner"), icon: LayoutDashboard, color: "bg-lime-500", text: "text-slate-950" },
        { label: t("team_slot_driver"), icon: Truck, color: "bg-red-600", text: "text-white" },
      ],
      inviteRoles: [{ label: t("team_slot_machinist"), icon: HardHat, color: "bg-amber-500", text: "text-slate-950" }],
    },
  ];

  const minRoles = [
    { label: t("team_slot_planner"), icon: LayoutDashboard, color: "bg-lime-500", text: "text-slate-950" },
    { label: t("team_slot_driver"), icon: Truck, color: "bg-red-600", text: "text-white" },
    { label: t("team_slot_machinist"), icon: HardHat, color: "bg-amber-500", text: "text-slate-950" },
  ];

  return (
    <section className="py-28 px-6 bg-white border-t border-slate-200" data-testid="team-assembly-section">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-[10px] font-bold text-red-500 uppercase tracking-[0.3em] block mb-6">{t("team_tag")}</span>
          <h2 className="text-4xl md:text-5xl tracking-tighter mb-4">
            <span className="font-light text-slate-400">{t("team_title_light")}</span>{" "}
            <span className="font-black text-slate-900">{t("team_title_bold")}</span>
          </h2>
          <p className="text-base text-slate-500 font-light max-w-xl mx-auto">{t("team_desc")}</p>
        </div>

        <div className="flex items-center justify-center gap-6 mb-14">
          {minRoles.map((role, i) => (
            <div key={i} className="flex flex-col items-center gap-2">
              <div className={`w-14 h-14 ${role.color} rounded-2xl flex items-center justify-center ${role.text} shadow-md`}>
                <role.icon className="w-7 h-7" strokeWidth={1.5} />
              </div>
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{role.label}</span>
            </div>
          ))}
        </div>
        <p className="text-center text-xs font-bold text-slate-400 uppercase tracking-widest mb-14">{t("team_minimum")}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
          {combos.map((combo, ci) => (
            <div key={ci} className="relative p-6 bg-slate-50 border border-slate-200 rounded-2xl hover:border-red-300 transition-all" data-testid={`team-combo-${ci}`}>
              <h3 className="text-lg font-black text-slate-900 tracking-tight mb-2">{combo.title}</h3>
              <p className="text-sm text-slate-500 font-light leading-relaxed mb-6">{combo.desc}</p>
              <div className="flex items-stretch gap-3">
                <div className="flex-1 p-4 bg-white border-2 border-lime-400 rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Monitor className="w-4 h-4 text-slate-400" />
                    <span className="text-[9px] font-black text-lime-600 uppercase tracking-widest">{combo.youLabel}</span>
                    <span className="text-[9px] text-slate-400 font-medium">({t("team_desktop")})</span>
                  </div>
                  <div className="space-y-2">
                    {combo.youRoles.map((role, ri) => (
                      <div key={ri} className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 ${role.color} rounded-lg flex items-center justify-center ${role.text}`}><role.icon className="w-4 h-4" /></div>
                        <span className="text-xs font-bold text-slate-800">{role.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col items-center justify-center px-1">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center"><Link2 className="w-4 h-4 text-red-500" /></div>
                  <div className="h-full w-px bg-slate-200 mt-1" />
                </div>
                <div className="flex-1 p-4 bg-white border-2 border-dashed border-slate-300 rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Smartphone className="w-4 h-4 text-slate-400" />
                    <span className="text-[9px] font-black text-red-500 uppercase tracking-widest">{combo.inviteLabel}</span>
                    <span className="text-[9px] text-slate-400 font-medium">({t("team_smartphone")})</span>
                  </div>
                  <div className="space-y-2">
                    {combo.inviteRoles.map((role, ri) => (
                      <div key={ri} className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 ${role.color} rounded-lg flex items-center justify-center ${role.text}`}><role.icon className="w-4 h-4" /></div>
                        <span className="text-xs font-bold text-slate-800">{role.label}</span>
                        <span className="text-[9px] bg-slate-100 text-slate-400 font-bold px-2 py-0.5 rounded-full">+n</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-start gap-3 p-4 bg-slate-50 border border-slate-200 rounded-xl">
            <Lock className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <p className="text-xs text-slate-500 leading-relaxed">{t("team_sole_planner")}</p>
          </div>
          <div className="flex items-start gap-3 p-4 bg-slate-50 border border-slate-200 rounded-xl">
            <Users className="w-5 h-5 text-lime-500 shrink-0 mt-0.5" />
            <p className="text-xs text-slate-500 leading-relaxed">{t("team_multi_note")}</p>
          </div>
          <div className="flex items-start gap-3 p-4 bg-slate-50 border border-slate-200 rounded-xl">
            <Globe className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
            <p className="text-xs text-slate-500 leading-relaxed">{t("team_join_note")}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
